/**
 * Hover and selection highlights.
 *  - Atoms/bonds: inflated unlit shells, +0.1 Å (HighlightThickness, RenderingViewModel.Atomistic.cs:39, 265-309),
 *    radius = max(atom radius, bondScale when bonded) + 0.1, drawn as mask -> band -> shell impostor passes with a
 *    stencil (ImpostorHighlightPasses.cs:7-11, 18-24, DepthBand 1.6 model units, skipped above 2000 instances).
 *  - Ribbons: inverse hull (front-culled, _Size 0.1) for the hovered/selected residues (RibbonRenderer.cs:35-72).
 * Hover colour #463DFF, selection white (WorkspaceSceneDiffViewModel.cs:42-49).
 */
import { Group, type Object3D } from 'three';
import { INTERACTION } from '@nanome-lite/look';
import type { AtomisticGeometry, CylinderInstances, SphereInstances } from './atomistic';
import { CylinderBatch, ImpostorMaterials, RENDER_ORDER, SphereBatch, impostorMesh } from './impostors';

/** Nanome skips the mask/band passes above this many highlight instances (ImpostorHighlightPasses.cs:23). */
export const HIGHLIGHT_MAX_INSTANCES = 2000;

export interface ShellInstances { spheres: SphereInstances; sphereRatio: Float32Array; cylinders: CylinderInstances; cylinderRatio: Float32Array }

/**
 * Shell instances for `atoms` (model atom indices) drawn by `geometry`. `bondAtoms` resolves a bond index to its two
 * atoms; a cylinder is outlined when both of its atoms are in the set.
 */
export function buildShellInstances(geometry: AtomisticGeometry, atoms: Set<number>, bondScale: number, hasBond: (atomIndex: number) => boolean, bondAtoms: (bondIndex: number) => [number, number]): ShellInstances {
  const t = INTERACTION.atomOutlineShellAngstrom;
  const sc: number[] = [], sr: number[] = [], scol: number[] = [], sa: number[] = [], sratio: number[] = [];
  const seen = new Set<number>();
  const g = geometry.spheres;
  for (let i = 0; i < g.count; i++) {
    const a = g.atomIndex[i]!;
    if (!atoms.has(a)) continue;
    seen.add(a);
    let r = g.radius[i]!;
    if (hasBond(a)) r = Math.max(r, bondScale);
    sc.push(g.center[i * 3]!, g.center[i * 3 + 1]!, g.center[i * 3 + 2]!); sr.push(r + t); scol.push(1, 1, 1); sa.push(a); sratio.push(r / (r + t));
  }
  // Atoms without a sphere (sticks/wire) still get a joint shell at the bond radius where bonds meet (:555-625).
  const c = geometry.cylinders;
  const cs: number[] = [], ce: number[] = [], cr: number[] = [], ccs: number[] = [], cce: number[] = [], cf: number[] = [], cb: number[] = [], cratio: number[] = [];
  for (let i = 0; i < c.count; i++) {
    const r = c.radius[i]!;
    const [a1, a2] = bondAtoms(c.bondIndex[i]!);
    if (!atoms.has(a1) || !atoms.has(a2)) continue;
    cs.push(c.start[i * 3]!, c.start[i * 3 + 1]!, c.start[i * 3 + 2]!); ce.push(c.end[i * 3]!, c.end[i * 3 + 1]!, c.end[i * 3 + 2]!);
    cr.push(r + t); ccs.push(1, 1, 1); cce.push(1, 1, 1); cf.push(c.flags[i]!); cb.push(c.bondIndex[i]!); cratio.push(r / (r + t));
  }
  return {
    spheres: { count: sa.length, center: Float32Array.from(sc), radius: Float32Array.from(sr), color: Float32Array.from(scol), atomIndex: Uint32Array.from(sa) },
    sphereRatio: Float32Array.from(sratio),
    cylinders: { count: cb.length, start: Float32Array.from(cs), end: Float32Array.from(ce), radius: Float32Array.from(cr), colorStart: Float32Array.from(ccs), colorEnd: Float32Array.from(cce), flags: Uint8Array.from(cf), bondIndex: Uint32Array.from(cb) },
    cylinderRatio: Float32Array.from(cratio),
  };
}

/** GPU state for one highlight colour (hover or selection). Lives under the scene root group. */
export class HighlightLayer {
  readonly group = new Group();
  private batches: { dispose(): void }[] = [];
  private readonly shell: { sphere: import('three').RawShaderMaterial; cylinder: import('three').RawShaderMaterial };

  constructor(private readonly materials: ImpostorMaterials, readonly colorHex: number) {
    this.group.matrixAutoUpdate = false;
    this.shell = materials.shell(colorHex);
  }

  clear(): void {
    for (const b of this.batches) b.dispose();
    this.batches = [];
    this.group.clear();
  }

  /** Replace the layer content with the given shells (several geometries may contribute). */
  set(shells: ShellInstances[]): void {
    this.clear();
    let total = 0;
    for (const s of shells) total += s.spheres.count + s.cylinders.count;
    const withMask = total <= HIGHLIGHT_MAX_INSTANCES;
    const add = (o: Object3D) => { o.matrixAutoUpdate = false; this.group.add(o); };
    for (const s of shells) {
      if (s.spheres.count > 0) {
        const b = new SphereBatch(s.spheres, 0, s.sphereRatio);
        this.batches.push(b);
        if (withMask) { add(impostorMesh(b.geometry, this.materials.sphere.mask, RENDER_ORDER.mask)); add(impostorMesh(b.geometry, this.materials.sphere.band, RENDER_ORDER.band)); }
        add(impostorMesh(b.geometry, this.shell.sphere, RENDER_ORDER.shell));
      }
      if (s.cylinders.count > 0) {
        const b = new CylinderBatch(s.cylinders, 0, s.cylinderRatio);
        this.batches.push(b);
        if (withMask) { add(impostorMesh(b.geometry, this.materials.cylinder.mask, RENDER_ORDER.mask)); add(impostorMesh(b.geometry, this.materials.cylinder.band, RENDER_ORDER.band)); }
        add(impostorMesh(b.geometry, this.shell.cylinder, RENDER_ORDER.shell));
      }
    }
  }

  dispose(): void { this.clear(); }
}
